// End-to-end encryption of voice/video frames over MoQ, the parley
// counterpart of livekit-client's E2EE (ExternalE2EEKeyProvider + frame
// cryptor). The gateway hands every e2ee_capable voice connection the room
// key (VOICE_SERVER_UPDATE.e2ee_key, rotated via VOICE_E2EE_KEY_UPDATE);
// the relay only ever forwards ciphertext. See docs/strands/e2ee.md.
//
// Frame layout (every media frame, after the WebCodecs encoder and the hang
// container, before MoQ):
//
//   AES-256-GCM(ciphertext ‖ 16 byte tag) ‖ IV (12) ‖ key id (1) ‖ "\xE2\xEEMQ"
//
// The AES key is HKDF-SHA-256(room key, salt "parley-moq-e2ee-v1"); the
// key id is one more HKDF byte, so every client derives the same id for a
// room key without a protocol field (receivers try every key with that id).
// The IV is a random 8 byte prefix per key and sender plus a 32 bit frame
// counter. The additional data binds a frame to its sender, broadcast and
// track, so the relay cannot move frames between participants or tracks.

const SALT = new TextEncoder().encode('parley-moq-e2ee-v1');
const KEY_INFO = new TextEncoder().encode('frame key');
const ID_INFO = new TextEncoder().encode('key id');
const MAGIC = [0xe2, 0xee, 0x4d, 0x51];
const IV_LENGTH = 12;
const TRAILER = IV_LENGTH + 1 + MAGIC.length;
const TAG_LENGTH = 16;
/** Keys a receiver keeps: the current one plus a few recent ones. */
const MAX_KEYS = 4;
/**
 * After a rotation, senders keep the previous key this long, so every
 * participant has the new key before frames use it (the gateway delivers it
 * to all of them at about the same time).
 */
export const SEND_SWITCH_DELAY_MS = 1000;
/** Replaced keys are forgotten this long after the switch. */
const RETAIN_OLD_KEY_MS = 10_000;

/** Frame counters (no key material), for diagnostics and the browser test. */
const e2eeStats = {
	encrypted: 0,
	decrypted: 0,
	/** Frames dropped: no key, wrong key, forged or tampered. */
	rejected: 0,
	/** Plaintext frames sent while E2EE was off. */
	plaintextSent: 0,
	/** Plaintext frames accepted while E2EE was off. */
	plaintextReceived: 0,
};
try {
	(globalThis as {__parleyE2EEStats?: typeof e2eeStats}).__parleyE2EEStats = e2eeStats;
} catch {}

interface KeyEntry {
	raw: string;
	id: number;
	key: CryptoKey;
	ivPrefix: Uint8Array<ArrayBuffer>;
	counter: number;
	retireTimer?: ReturnType<typeof setTimeout>;
}

function bytesOf(key: string | ArrayBuffer): Uint8Array<ArrayBuffer> {
	return typeof key === 'string' ? new TextEncoder().encode(key) : new Uint8Array(key.slice(0));
}

function rawOf(key: string | ArrayBuffer): string {
	if (typeof key === 'string') return key;
	let s = '';
	for (const b of new Uint8Array(key)) s += String.fromCharCode(b);
	return `bin:${s}`;
}

async function deriveEntry(input: string | ArrayBuffer): Promise<KeyEntry> {
	const base = await crypto.subtle.importKey('raw', bytesOf(input), 'HKDF', false, ['deriveKey', 'deriveBits']);
	const key = await crypto.subtle.deriveKey(
		{name: 'HKDF', hash: 'SHA-256', salt: SALT, info: KEY_INFO},
		base,
		{name: 'AES-GCM', length: 256},
		false,
		['encrypt', 'decrypt'],
	);
	const id = new Uint8Array(
		await crypto.subtle.deriveBits({name: 'HKDF', hash: 'SHA-256', salt: SALT, info: ID_INFO}, base, 8),
	)[0];
	const ivPrefix = crypto.getRandomValues(new Uint8Array(8));
	return {raw: rawOf(input), id, key, ivPrefix, counter: 0};
}

/**
 * The room key holder, API-compatible with livekit-client's
 * ExternalE2EEKeyProvider as Fluxer uses it (setKey with the gateway's
 * e2ee_key). One per Room.
 */
export class ExternalE2EEKeyProvider {
	#keys: Array<KeyEntry> = [];
	#send?: KeyEntry;
	#switchTimer?: ReturnType<typeof setTimeout>;
	/** performance.now() of the last setKey, to order early key updates (room.ts). */
	lastSetAt = 0;

	async setKey(key: string | ArrayBuffer): Promise<void> {
		this.lastSetAt = performance.now();
		const raw = rawOf(key);
		if (this.#keys.some((k) => k.raw === raw)) return;
		const entry = await deriveEntry(key);
		this.#keys.unshift(entry);
		for (const old of this.#keys.splice(MAX_KEYS)) clearTimeout(old.retireTimer);
		if (!this.#send) {
			this.#send = entry;
			return;
		}
		clearTimeout(this.#switchTimer);
		this.#switchTimer = setTimeout(() => {
			this.#send = entry;
			// A former participant knows the old keys: stop accepting them soon.
			for (const k of this.#keys) {
				if (k !== entry && !k.retireTimer) {
					k.retireTimer = setTimeout(() => {
						this.#keys = this.#keys.filter((x) => x !== k);
					}, RETAIN_OLD_KEY_MS);
				}
			}
		}, SEND_SWITCH_DELAY_MS);
	}

	/** @internal Encrypts one frame with the current key. */
	async encrypt(plain: Uint8Array, aad: Uint8Array): Promise<Uint8Array<ArrayBuffer> | undefined> {
		const entry = this.#send;
		if (!entry) return undefined;
		const iv = new Uint8Array(IV_LENGTH);
		iv.set(entry.ivPrefix, 0);
		new DataView(iv.buffer).setUint32(8, entry.counter);
		entry.counter = (entry.counter + 1) >>> 0;
		if (entry.counter === 0) entry.ivPrefix = crypto.getRandomValues(new Uint8Array(8));
		const sealed = new Uint8Array(
			await crypto.subtle.encrypt(
				{name: 'AES-GCM', iv, additionalData: aad as Uint8Array<ArrayBuffer>, tagLength: TAG_LENGTH * 8},
				entry.key,
				plain as Uint8Array<ArrayBuffer>,
			),
		);
		const out = new Uint8Array(sealed.byteLength + TRAILER);
		out.set(sealed, 0);
		out.set(iv, sealed.byteLength);
		out[sealed.byteLength + IV_LENGTH] = entry.id;
		out.set(MAGIC, sealed.byteLength + IV_LENGTH + 1);
		return out;
	}

	/** @internal Decrypts one frame; undefined if no known key opens it. */
	async decrypt(frame: Uint8Array, aad: Uint8Array): Promise<Uint8Array<ArrayBuffer> | undefined> {
		if (!isEncryptedFrame(frame)) return undefined;
		const end = frame.byteLength - TRAILER;
		const iv = frame.slice(end, end + IV_LENGTH);
		const id = frame[end + IV_LENGTH];
		const sealed = frame.subarray(0, end) as Uint8Array<ArrayBuffer>;
		for (const entry of this.#keys) {
			if (entry.id !== id) continue;
			try {
				return new Uint8Array(
					await crypto.subtle.decrypt(
						{name: 'AES-GCM', iv, additionalData: aad as Uint8Array<ArrayBuffer>, tagLength: TAG_LENGTH * 8},
						entry.key,
						sealed,
					),
				);
			} catch {
				// wrong key with the same id, or tampered: try the next one
			}
		}
		return undefined;
	}
}

/** Whether a frame carries the E2EE trailer (plaintext frames match with p = 2^-32). */
export function isEncryptedFrame(frame: Uint8Array): boolean {
	const n = frame.byteLength;
	if (n < TRAILER + TAG_LENGTH) return false;
	for (let i = 0; i < MAGIC.length; i++) if (frame[n - MAGIC.length + i] !== MAGIC[i]) return false;
	return true;
}

/** Media E2EE needs WebCrypto (AES-GCM, HKDF); WebCodecs is needed for MoQ anyway. */
export function isE2EESupported(): boolean {
	try {
		return (
			typeof crypto !== 'undefined' &&
			typeof crypto.subtle?.importKey === 'function' &&
			typeof crypto.subtle.deriveKey === 'function' &&
			typeof crypto.getRandomValues === 'function'
		);
	} catch {
		return false;
	}
}

/**
 * The E2EE state of one Room, shared by its publisher and subscribers:
 * the key provider (if the gateway gave us a key) and whether encryption is
 * on (Room.setE2EEEnabled; Fluxer turns it off when a participant without
 * E2EE, typically a bot, is in the room).
 */
export class MediaCrypto {
	readonly provider?: ExternalE2EEKeyProvider;
	enabled = false;

	constructor(provider?: ExternalE2EEKeyProvider) {
		this.provider = provider;
	}

	/** Encrypts an outgoing frame, or returns it unchanged while E2EE is off. */
	async seal(plain: Uint8Array, aad: Uint8Array): Promise<Uint8Array | undefined> {
		if (!this.enabled) {
			e2eeStats.plaintextSent++;
			return plain;
		}
		const out = await this.provider?.encrypt(plain, aad);
		if (out) e2eeStats.encrypted++;
		else e2eeStats.rejected++; // enabled without a key: send nothing rather than plaintext
		return out;
	}

	/** Decrypts an incoming frame; undefined means drop it. */
	async open(frame: Uint8Array, aad: Uint8Array): Promise<Uint8Array | undefined> {
		if (isEncryptedFrame(frame)) {
			const plain = await this.provider?.decrypt(frame, aad);
			if (plain) {
				e2eeStats.decrypted++;
				return plain;
			}
			// Without E2EE a plaintext frame may, very rarely, look encrypted.
			if (!this.enabled && this.provider) {
				e2eeStats.plaintextReceived++;
				return frame;
			}
			e2eeStats.rejected++;
			return undefined;
		}
		if (this.enabled) {
			// Unencrypted media in an encrypted room: could be injected by the relay.
			e2eeStats.rejected++;
			return undefined;
		}
		e2eeStats.plaintextReceived++;
		return frame;
	}
}

/** Additional authenticated data of a frame: who sent it, on which broadcast and track. */
export function frameAAD(identity: string, kind: string, track: string): Uint8Array {
	return new TextEncoder().encode(`parley-moq-e2ee-v1\n${identity}\n${kind}\n${track}`);
}

// Key rotation (VOICE_E2EE_KEY_UPDATE). A key update can arrive while the
// client is still setting up the room for this connection; it is kept
// until the room connects and applied if it came after the room's key.

interface KeyedRoom {
	connectionId(): string | undefined;
	e2eeKeyProvider(): ExternalE2EEKeyProvider | undefined;
}

const liveRooms = new Set<KeyedRoom>();
const pendingKeys = new Map<string, {key: string; at: number}>();

/** @internal */
export function registerKeyedRoom(room: KeyedRoom): () => void {
	liveRooms.add(room);
	const id = room.connectionId();
	const provider = room.e2eeKeyProvider();
	const pending = id ? pendingKeys.get(id) : undefined;
	if (id) pendingKeys.delete(id);
	if (pending && provider && pending.at > provider.lastSetAt) void provider.setKey(pending.key);
	return () => liveRooms.delete(room);
}

/** Applies a rotated room key (the VOICE_E2EE_KEY_UPDATE dispatch). */
export function applyRoomKeyUpdate(connectionId: string, key: string): void {
	// Every room of this connection, not just the first: while a session
	// switch is in flight (moderator move, another relay) the old room is
	// still registered, and the rotation belongs to the new one.
	let applied = false;
	for (const room of liveRooms) {
		if (room.connectionId() !== connectionId) continue;
		void room.e2eeKeyProvider()?.setKey(key);
		applied = true;
	}
	if (applied) return;
	pendingKeys.set(connectionId, {key, at: performance.now()});
	if (pendingKeys.size > 16) pendingKeys.delete(pendingKeys.keys().next().value as string);
}

/** Gateway handler for VOICE_E2EE_KEY_UPDATE (parley extension, registered in EventRouter). */
export function handleVoiceE2EEKeyUpdate(data: unknown): void {
	const d = data as {connection_id?: unknown; e2ee_key?: unknown} | null;
	if (typeof d?.connection_id === 'string' && typeof d.e2ee_key === 'string') {
		applyRoomKeyUpdate(d.connection_id, d.e2ee_key);
	}
}
